import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import { getPageSeo, breadcrumbSchema, caseStudySchema } from "../lib/seo";

const LaMarquetaCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#101F27] text-white min-h-screen">
      <SEO
        {...getPageSeo("/case-studies/la-marqueta")}
        schema={[
          caseStudySchema("/case-studies/la-marqueta"),
          breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Case Studies", path: "/case-studies" },
          { name: "La Marqueta", path: "/case-studies/la-marqueta" },
        ]),
        ]}
      />
      {/* Page Title Bar */}
      <section className="bg-[#37464F]/30 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <Link
                to="/case-studies"
                className="inline-flex items-center text-[#009688] hover:underline mb-6 text-sm font-semibold"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Case Studies
              </Link>
              <p className="text-sm uppercase tracking-wide text-[#009688] font-semibold mb-3">
                Cashless Solution
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                La Marqueta, Puerto Rico: Agility and Improved User Experience with NFC Wristbands
              </h1>
            </div>
            <div className="lg:col-span-3">
              <div
                className="w-full h-72 md:h-96 rounded-lg bg-cover bg-center shadow-2xl"
                style={{
                  backgroundImage:
                    "url('/images/case-studies/la-marqueta.jpg')",
                }}
                role="img"
                aria-label="La Marqueta"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Overview</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              La Marqueta nightclub in San Juan, Puerto Rico, located in the heart of Condado, is a unique seaside hideaway offering a diverse gastronomic experience and exquisite cocktails. The establishment faced challenges stemming from the use of paper drink tickets, resulting in losses from multiple redemptions. In search of a more efficient and secure solution, the nightclub sought to address these issues using nfc wristbands.
            </p>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Challenges</h2>
            <div className="md:flex md:items-start md:gap-8 mb-10">
              <div className="flex-1">
                <p className="text-lg text-gray-300 leading-relaxed mb-4">
                  La Marqueta encountered three main challenges with paper tickets:
                </p>
                <p className="text-lg text-gray-300 leading-relaxed mb-4">
                  <strong className="text-white">Inefficiency</strong>: Handling paper tickets proved time-consuming, particularly during peak hours, leading to long lines and customer frustration.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed mb-4">
                  <strong className="text-white">Tracking issues</strong>: Paper tickets posed difficulties in reliable sales and inventory tracking, hindering effective management analysis.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  <strong className="text-white">Risk of loss</strong>: Misplaced paper tickets could result in disputes, prompting the need for a more secure solution.
                </p>
              </div>
              <img
                src="/images/case-studies/la-marqueta-2.png"
                alt=""
                className="w-60 h-auto rounded-lg shadow-xl mx-auto md:mx-0 mt-6 md:mt-0 flex-shrink-0"
              />
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Solution</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              To overcome these challenges, La Marqueta implemented WristCoin's cashless payment system, replacing traditional paper tickets. This new system offered several advantages, including:
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              WristCoin provided <strong className="text-white">valuable purchasing data</strong> and enhanced security, addressing the tracking issues faced with paper tickets. The introduction of "hybrid" mode wristbands allowed guests to self-serve or use a cashier, promoting <strong className="text-white">flexibility, agility, and an improved user experience</strong>.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              WristCoin supported <strong className="text-white">sustainability</strong> through a wristband recycling system, aligning with the growing trend towards eco-friendly practices.
            </p>

            <div className="relative w-full mb-10 rounded-lg overflow-hidden shadow-2xl" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/8i9POIPEfQg"
                title="WristCoin Self Serve Online Topup Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Outcome</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              The implementation of WristCoin yielded improvements for La Marqueta nightclub. The switch led to a reduction in drink ticket misuse, resulting in 23% increased revenue for the establishment. The convenience of self-service contributed to higher conversion rates, enhancing the overall customer experience.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-10">
              La Marqueta nightclub's partnership with WristCoin streamlined operations, minimized losses, and significantly enhanced the overall guest experience. The innovative technology not only increased revenue but also aligned with sustainability goals, marking a key contribution to the club's success. WristCoin's solution effectively addressed La Marqueta's key challenges, showcasing its potential as a comprehensive and valuable solution for similar establishments.
            </p>

            <div className="relative w-full mb-10 rounded-lg overflow-hidden shadow-2xl" style={{ paddingTop: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/Wml8d_pgu7g"
                title="WristCoin: Flexibilidad, Agilidad y Excelente Experiencia con Cashless en Puerto Rico"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default LaMarquetaCaseStudy;
